import { DB_TYPE } from '@/common/define/common.define';
import {
  getGetV7TiberoActiveSessionDetailSqlTextQueryKey,
  getV7TiberoActiveSessionDetailSqlText,
} from '@/api-client/tibero/queries/tibero-controller/tibero-controller';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';
import { formatTimeRange } from '@/database/utils/format.utils';
import { DATABASE_DETAIL_TYPE, DOCUMENT_TEXT_TYPE } from '@/database/adapter/openDetail.define';
import type {
  DocumentTextParams,
  ParameterDetailParams,
  SessionDetailParams,
  SqlDetailParams,
} from '../types';
import { formatName, mergeDetailInfo, parseSchema } from './utils';
import {
  sessionDetailSchema,
  sqlDetailSchema,
  parameterDetailSchema,
  documentTextSchema,
  type SessionDetailSchema,
  type TiberoSessionDetailAddition,
  type SqlDetailSchema,
  type TiberoSqlDetailAddition,
  type ParameterDetailSchema,
  type TiberoParameterDetailAddition,
  type DocumentTextSchema,
} from './tibero.schema';

export const getTiberoSessionDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  isOpenedFromPA,
  init,
  instanceId,
}: SessionDetailParams): { name: string; addition: TiberoSessionDetailAddition } => {
  const { data } = parseSchema<SessionDetailSchema>(
    sessionDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime, isOpenedFromPA, init }),
    DATABASE_DETAIL_TYPE.SESSION,
  );

  return {
    name: formatName(data.sid),
    addition: {
      dbType: DB_TYPE.TIBERO,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      sid: data.sid,
      serial: data.serial,
      childNumber: data.childNumber,
      planHashValue: data.planHashValue,
      hashValue: data.hashValue,
      isOpenedFromPA: data.isOpenedFromPA,
      init: data.init,
      collectTime: data.collectTime,
    },
  };
};

export const getTiberoSqlDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: SqlDetailParams): { name: string; addition: TiberoSqlDetailAddition } => {
  const { data } = parseSchema<SqlDetailSchema>(
    sqlDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.SQL,
  );

  return {
    name: formatName(data.sqlId),
    addition: {
      dbType: DB_TYPE.TIBERO,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      sqlId: data.sqlId,
      module: data.module,
      parsingSchemaName: data.parsingSchemaName,
      planHashValue: data.planHashValue,
      hashValue: data.hashValue,
      childNumber: data.childNumber,
      collectTime: data.collectTime,
    },
  };
};

export const getTiberoParameterDetailAddition = ({
  detailInfo,
  instanceId,
}: ParameterDetailParams): { name: string; addition: TiberoParameterDetailAddition } => {
  const { data } = parseSchema<ParameterDetailSchema>(
    parameterDetailSchema,
    mergeDetailInfo(detailInfo, instanceId),
    DATABASE_DETAIL_TYPE.PARAMETER,
  );

  return {
    name: formatName(data.parameterName),
    addition: {
      dbType: DB_TYPE.TIBERO,
      instanceId: data.instanceId,
    },
  };
};

export const getTiberoDocumentTextParams = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
  period,
  useFieldValidation,
}: DocumentTextParams) => {
  const formattedTime = formatTimeRange({ fromTime, toTime });
  const timeInfo = period ? { period } : formattedTime;

  if (!useFieldValidation) {
    return {
      instanceId,
      ...timeInfo,
      sqlId: detailInfo.sqlId,
    };
  }

  const { data } = parseSchema<DocumentTextSchema>(
    documentTextSchema,
    mergeDetailInfo(detailInfo, instanceId),
    DOCUMENT_TEXT_TYPE,
  );

  return {
    instanceId: data.instanceId,
    ...timeInfo,
    sqlId: data.sqlId,
  };
};

export const fetchTiberoDocumentText = async ({
  frameName,
  abortController,
  ...rest
}: DocumentTextParams) => {
  const params = getTiberoDocumentTextParams({ ...rest, useFieldValidation: true });

  const {
    data: { data = [] },
  } = await getV7TiberoActiveSessionDetailSqlText(
    params,
    { params: { frameName: frameName || FRAME_NAMES.DOCUMENT_WINDOW.FULL_TEXT } },
    abortController?.signal,
  );

  return data[0]?.sqlText || null;
};

export const getTiberoDocumentTextQueryKey = (params: DocumentTextParams | null) => {
  if (!params) {
    return getGetV7TiberoActiveSessionDetailSqlTextQueryKey();
  }

  const convertedParams = getTiberoDocumentTextParams({ ...params, useFieldValidation: false });

  return getGetV7TiberoActiveSessionDetailSqlTextQueryKey(convertedParams);
};

export const tiberoSqlDetailRequiredMap = getRequiredMap(sqlDetailSchema);
export const tiberoSessionDetailRequiredMap = getRequiredMap(sessionDetailSchema);
export const tiberoDocumentTextRequiredMap = getRequiredMap(documentTextSchema);
